import React, { FC, Fragment, useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { withRouter } from '@/checkout-instore-legacy/utils/withRouter'
import Button from '@vtex/styleguide/lib/Button'
import ModalDialog from '@vtex/styleguide/lib/ModalDialog'

import { withMediaQuery } from '@/checkout-instore-legacy/utils/mediaQuery'
import { getOrderGroupId } from '@/checkout-instore-legacy/order/utils'
import { logClickEvent } from '@/checkout-instore-legacy/utils/EventHelper'
import { useDispatch, useSelector } from 'react-redux'
import { cancelGivenOrder } from '@/checkout-instore-legacy/order/actions'
import { orderSelector } from '@/checkout-instore-legacy/order/selectors'

interface CancelOrderButtonProps {
  id: string
  orderId: string
  labelsIds: {
    cancelOrderLabelId: string
    cancelModalConfirmActionId: string
    cancelOrderModalTitleId: string
    cancelModalTextPart1Id: string
    cancelModalTextPart2Id: string
  }
  cancelAllOrderGroup?: boolean
}

interface CancelPartialOrderButtonProps {
  orderId: string
}

type CancelTotalOrderButtonProps = CancelPartialOrderButtonProps

type WithRouterPropsCancelOrderButtonProps = CancelOrderButtonProps & {
  router: RouterProps<any, any>
}

const CancelOrderButton: FC<CancelOrderButtonProps> = withRouter(
  withMediaQuery<WithRouterPropsCancelOrderButtonProps>(
    ({ id, labelsIds, orderId, mediaQuery, cancelAllOrderGroup = false }) => {
      const {
        cancelOrder: { loading },
      } = useSelector(orderSelector)

      const dispatch = useDispatch()

      const [isModalOpen, setIsModalOpen] = useState(false)

      const buttonSize = mediaQuery({
        phone: cancelAllOrderGroup ? 'regular' : 'small',
        tablet: 'regular',
      })

      const fullWidth = mediaQuery({
        phone: true,
        tablet: true,
        desktop: false,
      })

      const blockModalButtons = mediaQuery({
        phone: true,
        tablet: false,
      })

      useEffect(() => {
        if (loading === false) {
          closeModal()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [loading])

      const openModal = () => {
        logClickEvent('open-cancel-order-modal', 'Sale', { orderId })
        setIsModalOpen(true)
      }

      const closeModal = () => {
        logClickEvent('close-cancel-order-modal', 'Sale', { orderId })
        setIsModalOpen(false)
      }

      const cancelOrderOrOrderGroup = () => {
        logClickEvent('confirm-cancel-order-modal', 'Sale', { orderId })

        if (cancelAllOrderGroup) {
          return dispatch(
            cancelGivenOrder({
              orderId: null,
              orderGroupId: getOrderGroupId(orderId),
            })
          )
        }

        return dispatch(
          cancelGivenOrder({
            orderId,
            orderGroupId: null,
          })
        )
      }

      return (
        <Fragment>
          <Button
            id={id}
            testId={id}
            variation={cancelAllOrderGroup ? 'danger' : 'danger-tertiary'}
            size={buttonSize}
            block={fullWidth}
            onClick={openModal}
          >
            <FormattedMessage id={labelsIds.cancelOrderLabelId} />
          </Button>
          <ModalDialog
            centered
            loading={loading}
            confirmation={{
              onClick: cancelOrderOrOrderGroup,
              label: (
                <FormattedMessage id={labelsIds.cancelModalConfirmActionId} />
              ),
              isDangerous: true,
              block: blockModalButtons,
              testId: 'cancel-order-confirm-btn',
            }}
            cancelation={{
              onClick: closeModal,
              label: <FormattedMessage id="cancelModalExitAction" />,
              block: blockModalButtons,
              testId: 'cancel-order-exit-btn',
            }}
            isOpen={isModalOpen}
            onClose={closeModal}
          >
            <div>
              <h2 className="normal fw4 mv5">
                <FormattedMessage id={labelsIds.cancelOrderModalTitleId} />
              </h2>
              <p>
                <FormattedMessage id={labelsIds.cancelModalTextPart1Id} />
              </p>
              <p>
                <FormattedMessage id={labelsIds.cancelModalTextPart2Id} />
              </p>
            </div>
          </ModalDialog>
        </Fragment>
      )
    }
  )
) as any

export const CancelPartialOrderButton: FC<CancelPartialOrderButtonProps> = ({
  orderId,
}) => {
  const labelsIds = {
    cancelOrderLabelId: 'cancelPartialOrderLabel',
    cancelModalConfirmActionId: 'cancelPartialOrderLabel',
    cancelOrderModalTitleId: 'cancelPartialOrderLabel',
    cancelModalTextPart1Id: 'cancelModalTextPart1',
    cancelModalTextPart2Id: 'cancelModalPartialOrderTextPart2',
  }
  return (
    <CancelOrderButton
      id="cancel-order"
      orderId={orderId}
      labelsIds={labelsIds}
      cancelAllOrderGroup={false}
    />
  )
}

export const CancelTotalOrderButton: FC<CancelTotalOrderButtonProps> = ({
  orderId,
}) => {
  const labelsIds = {
    cancelOrderLabelId: 'cancelFullOrderLabel',
    cancelModalConfirmActionId: 'cancelFullOrderLabel',
    cancelOrderModalTitleId: 'cancelFullOrderLabel',
    cancelModalTextPart1Id: 'cancelModalTextPart1',
    cancelModalTextPart2Id: 'cancelModalFullOrderTextPart2',
  }
  return (
    <CancelOrderButton
      id="cancel-total-order"
      orderId={orderId}
      labelsIds={labelsIds}
      cancelAllOrderGroup
    />
  )
}
